import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Briefcase,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Plus,
  List,
  Key,
  FileCheck,
  Shield,
  Clock,
  CornerUpLeft,
  UserCheck,
  AlertCircle,
  ArrowLeft,
  MessageSquare,
  Trash2,
  Edit,
  Eye,
  CheckCircle,
  Award,
  UserCog,
  Crown,
  Calculator,
  DollarSign,
  RefreshCw,
  Printer,
  CalendarOff,
  CalendarDays,
  BadgeCheck,
  ClipboardCheck,
  PenLine,
  Network,
  ScrollText,
  Sparkles,
  LayoutDashboard,
  GraduationCap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, UI_THEMES } from '../contexts/ThemeContext';
import api from '../services/api';
import ChangePasswordModal from './ChangePasswordModal';
import KadryEmailModal from './KadryEmailModal';
import ChatWidget from './ChatWidget';

const SIDEBAR_COLLAPSED_KEY = 'crm-sidebar-collapsed';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [invoicesMenuOpen, setInvoicesMenuOpen] = useState(true);
  const [casesMenuOpen, setCasesMenuOpen] = useState(false);
  const [departmentsMenuOpen, setDepartmentsMenuOpen] = useState(false);
  const [usersMenuOpen, setUsersMenuOpen] = useState(false);
  const [hrMenuOpen, setHrMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [invoiceCounts, setInvoiceCounts] = useState({
    inProgress: 0,
    assigned: 0,
    completed: 0,
    done: 0,
    unread: 0,
    new: 0,
    returnedTotal: 0,
    signed: 0,
    pending: 0
  });
  const [hasNewInvoices, setHasNewInvoices] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showKadryEmailModal, setShowKadryEmailModal] = useState(false);
  const [refreshingPermissions, setRefreshingPermissions] = useState(false);
  const { user, logout, refreshUser, returnToSuperAdmin, isImpersonating, originalSuperAdmin } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Aktualizuj datę i godzinę co sekundę
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user?.requiresMfaSetup && !location.pathname.startsWith('/app/mfa-settings')) {
      navigate('/app/mfa-settings', { replace: true });
    }
  }, [user?.requiresMfaSetup, location.pathname, navigate]);

  // Formatuj datę i godzinę
  const formatDateTime = (date) => {
    const options = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false
    };
    return date.toLocaleString('pl-PL', options);
  };

  useEffect(() => {
    if (!user) return;

    let initialUnread = null;

    const fetchCounts = async () => {
      try {
        const response = await api.get('/invoices/counts', {
          timeout: 10000 // 10 sekund timeout
        });
        const newUnread = response.data?.unread || 0;
        
        // Sprawdź czy są nowe faktury (unread wzrosło od ostatniego sprawdzenia)
        setInvoiceCounts(prevCounts => {
          // Jeśli to pierwsze sprawdzenie, zapisz wartość początkową
          if (initialUnread === null) {
            initialUnread = prevCounts.unread || newUnread;
          }
          
          // Jeśli unread wzrosło od ostatniego sprawdzenia, pokaż wskaźnik
          if (prevCounts.unread !== undefined && newUnread > prevCounts.unread) {
            setHasNewInvoices(true);
          }
          
          return {
            inProgress: response.data?.inProgress || 0,
            assigned: response.data?.assigned || 0,
            completed: response.data?.completed || 0,
            done: response.data?.done || 0,
            unread: newUnread,
            new: response.data?.new || 0,
            returnedTotal: response.data?.returnedTotal || 0,
            signed: response.data?.signed || 0,
            pending: response.data?.pending || 0
          };
        });
      } catch (error) {
        // Nie loguj błędów "Request aborted" - to normalne gdy komponent się unmountuje
        const isAborted = error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.message === 'canceled';
        if (!isAborted) {
          console.error('Error fetching invoice counts:', error);
        }
      }
    };

    // Wymuś natychmiastowe odświeżenie po zmianie użytkownika
    fetchCounts();
    // Aktualizuj co 10 sekund zamiast 30, żeby szybciej wykrywać nowe faktury
    const interval = setInterval(fetchCounts, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Zmieniono z [user] na [user?.id] aby wymusić odświeżenie przy zmianie użytkownika

  // Funkcja sprawdzająca czy użytkownik ma dostęp do sekcji
  const hasAccess = (section) => {
    if (!user) return false;
    // Super Admin ma dostęp do wszystkiego
    if (user.role === 'SUPERADMIN') return true;

    if (user.isPreviewMode && ['settings', 'users', 'departments'].includes(section)) {
      return false;
    }
    
    // Sprawdzanie uprawnień dla innych użytkowników
    switch (section) {
      case 'cases':
        return user.canAccessCases === true;
      case 'departments':
        return user.canAccessDepartments === true;
      case 'users':
        return user.canAccessUsers === true;
      case 'settings':
        return user.canAccessSettings === true;
      case 'hr':
        return user.canAccessHr === true;
      case 'prints':
        return user.canAccessPrints === true;
      // Moduły dodatkowe dla nowych kont (obecni użytkownicy mają je włączone migracją)
      case 'invoices':
        return user.canAccessInvoices === true;
      case 'substitutions':
        return user.canAccessSubstitutions === true;
      case 'mfaSettings':
        return user.canAccessMfaSettings === true;
      default:
        return true; // Dashboard, Zgłoszenia i Kadry -> Mój grafik są dostępne dla wszystkich
    }
  };

  const allNavigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: Home },
    ...(hasAccess('invoices') ? [{
      name: 'Faktury',
      href: '/app/invoices/list',
      icon: FileText,
      submenu: [
        { name: 'Lista faktur', href: '/app/invoices/list', icon: List, count: invoiceCounts.new },
        { name: 'W realizacji', href: '/app/invoices/status/in-progress', icon: FileCheck, count: invoiceCounts.inProgress },
        { name: 'Oczekujące', href: '/app/invoices/status/pending', icon: Clock, count: invoiceCounts.pending },
        { name: 'Przekazane', href: '/app/invoices/status/assigned', icon: FileCheck, count: invoiceCounts.assigned },
        { name: 'Zakończone', href: '/app/invoices/status/completed', icon: FileCheck, count: invoiceCounts.completed },
        ...(user?.canViewAccountedInvoices ? [{ name: 'Zaksięgowane', href: '/app/invoices/status/done', icon: FileCheck, count: invoiceCounts.done, color: 'yellow' }] : []),
        { name: 'Zwroty', href: '/app/invoices/returns', icon: CornerUpLeft, count: invoiceCounts.returnedTotal },
        ...(user?.canViewSignedInvoices ? [{ name: 'Podpisane', href: '/app/invoices/status/signed', icon: FileCheck, count: invoiceCounts.signed || 0 }] : []),
        ...(user?.canDeleteInvoices ? [{ name: 'Kosz', href: '/app/invoices/trash', icon: Trash2, color: 'red' }] : []),
      ]
    }] : []),
    ...((user?.canAccessUmowyCru === true || user?.role === 'SUPERADMIN') ? [{ name: 'Umowy CRU', href: '/app/invoices/cru', icon: ScrollText }] : []),
    ...(hasAccess('cases') ? [{
      name: 'Sprawy', 
      href: '/app/cases/status/new', 
      icon: Briefcase,
      submenu: [
        { name: 'Nowe', href: '/app/cases/status/new', icon: Plus },
        { name: 'W realizacji', href: '/app/cases/status/in-progress', icon: FileCheck },
        { name: 'Przekazane', href: '/app/cases/status/assigned', icon: FileCheck },
        { name: 'Zakończone', href: '/app/cases/status/done', icon: FileCheck },
      ]
    }] : []),
    ...(hasAccess('departments') ? [{
      name: 'Działy', 
      href: '/app/departments/list', 
      icon: Building2,
      submenu: [
        { name: 'Lista działów', href: '/app/departments/list', icon: List },
        { name: 'Dodaj dział', href: '/app/departments/add', icon: Plus },
      ]
    }] : []),
    ...(hasAccess('users') ? [{
      name: 'Użytkownicy', 
      href: '/app/users/list', 
      icon: Users,
      submenu: [
        { name: 'Lista użytkowników', href: '/app/users/list', icon: List },
        { name: 'Dodaj użytkownika', href: '/app/users/add', icon: Plus },
      ]
    }] : []),
    ...(user ? [{
      name: 'Kadry',
      href: hasAccess('hr') ? '/app/hr/employees/list' : '/app/hr/grafik',
      icon: UserCheck,
      submenu: [
        ...(hasAccess('hr') ? [
          { name: 'Lista pracowników', href: '/app/hr/employees/list', icon: List },
          { name: 'Dodaj pracownika', href: '/app/hr/employees/add', icon: Plus },
        ] : []),
        // "Mój grafik" - dostępny dla każdego zalogowanego (zakres "każdy swój")
        { name: 'Mój grafik', href: '/app/hr/grafik', icon: CalendarDays },
        // Zatwierdzanie wniosków urlopowych - kadry (uprawnienie canAccessLeaveApprovals)
        // Podpis przełożonego / zastępcy
        ...(user?.isLeaveSupervisor === true ? [
          { name: 'Wnioski do podpisu', href: '/app/hr/wnioski-do-podpisu', icon: PenLine },
        ] : []),
        ...(user?.canAccessLeaveApprovals === true || user?.role === 'SUPERADMIN' ? [
          { name: 'Wnioski urlopowe', href: '/app/hr/wnioski-urlopowe', icon: ClipboardCheck },
          { name: 'Przełożeni', href: '/app/hr/przelozeni', icon: Network },
        ] : []),
        ...(user?.canAccessDoctorAbsences === true || user?.role === 'SUPERADMIN' ? [
          { name: 'Nieobecności lekarzy', href: '/app/hr/doctor-absences', icon: CalendarOff },
        ] : []),
      ]
    }] : []),
    // Zgłoszenia - dostępne dla wszystkich użytkowników
    { name: 'Zgłoszenia', href: '/app/tickets', icon: MessageSquare },
    // Zastępstwa - moduł dodatkowy (nowe konta bez uprawnienia go nie widzą)
    ...(hasAccess('substitutions') ? [{ name: 'Zastępstwa', href: '/app/substitutions', icon: Clock }] : []),
    ...(hasAccess('prints') ? [{ name: 'Wydruki', href: '/app/prints', icon: Printer }] : []),
    ...((user?.canAccessTrainings === true || user?.role === 'SUPERADMIN' || user?.role === 'ADMIN') ? [{ name: 'Szkolenia', href: '/app/trainings', icon: GraduationCap }] : []),
    // Podpis elektroniczny (konfiguracja MFA) - pozycja w menu jako moduł dodatkowy.
    // Sama trasa /app/mfa-settings pozostaje dostępna, bo służy też obowiązkowej
    // konfiguracji MFA po logowaniu.
    ...(hasAccess('mfaSettings') ? [{ name: 'Podpis elektroniczny', href: '/app/mfa-settings', icon: Shield }] : []),
    ...(hasAccess('settings') ? [{
      name: 'Ustawienia', 
      href: '/app/settings', 
      icon: Settings,
      submenu: [
        { name: 'Wszystkie ustawienia', href: '/app/settings', icon: Settings },
        { name: 'Faktury z KSeF', href: '/app/ksef/invoices', icon: FileText },
      ]
    }] : []),
    ...((user?.role === 'SUPERADMIN' || user?.role === 'ADMIN') ? [{
      name: 'Bezpieczeństwo',
      href: '/app/security-logs',
      icon: Shield,
    }] : []),
  ];

  const navigation = user?.requiresMfaSetup
    ? [{ name: 'Konfiguracja MFA', href: '/app/mfa-settings', icon: Shield }]
    : allNavigation;

  const isCurrentPath = (path) => {
    return location.pathname === path;
  };

  // Funkcja zwracająca listę aktywnych uprawnień użytkownika z ikonami
  const getActivePermissions = () => {
    if (!user) return [];
    
    const permissions = [];
    
    // Definicja uprawnień z ikonami i tooltipami
    const permissionMap = [
      { key: 'canAccessSettings', icon: Settings, label: 'Ustawienia', color: 'text-gray-600' },
      { key: 'canAccessUsers', icon: Users, label: 'Użytkownicy', color: 'text-blue-600' },
      { key: 'canAccessDepartments', icon: Building2, label: 'Działy', color: 'text-green-600' },
      { key: 'canAccessCases', icon: Briefcase, label: 'Sprawy', color: 'text-purple-600' },
      { key: 'canAccessHr', icon: UserCheck, label: 'Kadry', color: 'text-indigo-600' },
      { key: 'canAccessDoctorAbsences', icon: CalendarOff, label: 'Nieobecności lekarzy', color: 'text-indigo-500' },
      { key: 'canAccessLeaveApprovals', icon: ClipboardCheck, label: 'Zatwierdzanie wniosków urlopowych', color: 'text-blue-500' },
      { key: 'canAccessPrints', icon: Printer, label: 'Wydruki', color: 'text-slate-600' },
      { key: 'canAccessTrainings', icon: GraduationCap, label: 'Zarządzanie szkoleniami', color: 'text-indigo-600' },
      { key: 'canAccountInvoice', icon: Calculator, label: 'Księgowanie faktur', color: 'text-yellow-600' },
      { key: 'isManager', icon: UserCog, label: 'Kierownik', color: 'text-orange-600' },
      { key: 'isDirector', icon: Crown, label: 'Dyrektor', color: 'text-red-600' },
      { key: 'isPreviewMode', icon: Eye, label: 'Tryb podglądu', color: 'text-slate-600' },
      { key: 'canCheckMerit', icon: Award, label: 'Weryfikacja merytoryczna', color: 'text-pink-600' },
      { key: 'canDoPreliminaryCheck', icon: CheckCircle, label: 'Weryfikacja wstępna', color: 'text-teal-600' },
      { key: 'canCheckFormalAccounting', icon: FileCheck, label: 'Weryfikacja formalna', color: 'text-cyan-600' },
      { key: 'canAcceptInvoices', icon: CheckCircle, label: 'Akceptacja faktur', color: 'text-green-600' },
      { key: 'canViewSignedInvoices', icon: Eye, label: 'Podgląd podpisanych faktur', color: 'text-emerald-600' },
      { key: 'canViewAccountedInvoices', icon: FileText, label: 'Podgląd zaksięgowanych faktur', color: 'text-amber-600' },
      { key: 'canDeleteDescriptionsAndSignatures', icon: Trash2, label: 'Usuwanie opisów i podpisów', color: 'text-red-600' },
      { key: 'canDeleteInvoices', icon: Trash2, label: 'Usuwanie faktur', color: 'text-red-700' },
      { key: 'canEditInvoices', icon: Edit, label: 'Edycja faktur', color: 'text-blue-600' },
      { key: 'canUseCostCenter', icon: DollarSign, label: 'Centra kosztów', color: 'text-green-700' }
    ];
    
    permissionMap.forEach(({ key, icon, label, color }) => {
      if (user[key] === true) {
        permissions.push({ key, icon, label, color });
      }
    });
    
    return permissions;
  };

  // Funkcja do ręcznego odświeżenia uprawnień zastępstw
  const handleRefreshSubstitutionPermissions = async () => {
    try {
      setRefreshingPermissions(true);
      await api.post('/substitutions/refresh-permissions');
      // Można dodać toast notification jeśli jest dostępny
      alert('Uprawnienia zastępstw zostały zaktualizowane');
    } catch (error) {
      console.error('Błąd podczas odświeżania uprawnień:', error);
      alert('Błąd podczas odświeżania uprawnień: ' + (error.response?.data?.message || error.message));
    } finally {
      setRefreshingPermissions(false);
    }
  };

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch (_) {}
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg border-b border-gray-200">
        <div className="h-16 flex items-center justify-between px-4">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="h-20 w-20 flex items-center justify-center flex-shrink-0">
                <img 
                  src="/images/cropped-logo-szpital_akredytacja.png"
                  alt="Szpital Łapy Logo" 
                  className="h-20 w-20 object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  CRM LAPY{' '}
                  <span className="text-sm font-normal text-gray-500">wersja 2026.1</span>
                </h1>
              </div>
            </div>

            {/* Przełącznik wyglądu (klasyczny / nowoczesny) */}
            <div
              className="flex items-center p-0.5 bg-gray-100 rounded-lg border border-gray-200 flex-shrink-0"
              role="group"
              aria-label="Wygląd interfejsu"
            >
              <button
                type="button"
                onClick={() => setTheme(UI_THEMES.CLASSIC)}
                className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  theme === UI_THEMES.CLASSIC
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Klasyczny wygląd"
                aria-pressed={theme === UI_THEMES.CLASSIC}
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden xl:inline">Klasyczny</span>
              </button>
              <button
                type="button"
                onClick={() => setTheme(UI_THEMES.MODERN)}
                className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  theme === UI_THEMES.MODERN
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Nowoczesny wygląd"
                aria-pressed={theme === UI_THEMES.MODERN}
              >
                <Sparkles className="h-4 w-4" />
                <span className="hidden xl:inline">Nowoczesny</span>
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Data i godzina */}
            <div className="hidden lg:flex items-center space-x-2 px-3 py-1 bg-gray-50 rounded-md border border-gray-200">
              <Clock className="h-4 w-4 text-gray-500" />
              <div className="text-sm">
                <div className="font-medium text-gray-700">{formatDateTime(currentDateTime)}</div>
              </div>
            </div>
            
            {/* Ikony uprawnień */}
            {user && getActivePermissions().length > 0 && (
              <div className="hidden lg:flex items-center space-x-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200">
                {getActivePermissions().map((perm) => {
                  const Icon = perm.icon;
                  return (
                    <div
                      key={perm.key}
                      className="group relative"
                      title={perm.label}
                    >
                      <Icon className={`h-4 w-4 ${perm.color} hover:scale-110 transition-transform cursor-help`} />
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {perm.label}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Przycisk odświeżania uprawnień zastępstw (tylko dla superadmina) */}
            {isSuperAdmin && (
              <button
                onClick={handleRefreshSubstitutionPermissions}
                disabled={refreshingPermissions}
                className="hidden lg:flex items-center space-x-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Odśwież uprawnienia zastępstw"
              >
                <RefreshCw className={`h-4 w-4 text-blue-600 ${refreshingPermissions ? 'animate-spin' : ''}`} />
                <span className="text-xs text-blue-700 font-medium">Odśwież zastępstwa</span>
              </button>
            )}
            
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {(user?.firstName?.charAt(0) || user?.lastName?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-700">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user?.email || 'Użytkownik'}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            {/* Informacja o zastępstwie */}
            {user?.activeSubstitutions && user.activeSubstitutions.length > 0 && (
              <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-md border border-blue-200">
                <Clock className="h-4 w-4 text-blue-600" />
                <div className="text-xs">
                  <div className="font-medium text-blue-700">
                    Zastępstwo: {user.activeSubstitutions[0].managerName}
                  </div>
                  <div className="text-blue-600">
                    do {new Date(user.activeSubstitutions[0].endDate).toLocaleDateString('pl-PL')}
                  </div>
                </div>
              </div>
            )}
            
            {isImpersonating && originalSuperAdmin && (
              <button
                onClick={async () => {
                  try {
                    const result = await returnToSuperAdmin();
                    if (result.success) {
                      navigate('/app/dashboard');
                    } else {
                      console.error('Błąd powrotu do super admina:', result.error);
                    }
                  } catch (error) {
                    console.error('Błąd powrotu do super admina:', error);
                  }
                }}
                className="p-2 rounded-full text-orange-600 hover:text-orange-700 hover:bg-orange-50 transition-colors"
                title={`Powrót do super admina: ${originalSuperAdmin.firstName} ${originalSuperAdmin.lastName || ''} (${originalSuperAdmin.email})`}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => setShowKadryEmailModal(true)}
              className={`p-2 rounded-full transition-colors ${
                user?.kadryEmail
                  ? 'text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'
                  : 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
              }`}
              title={user?.kadryEmail ? `E-mail kadrowy: ${user.kadryEmail}` : 'Ustaw e-mail kadrowy (grafik)'}
            >
              <BadgeCheck className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowChangePasswordModal(true)}
              className="p-2 rounded-full text-gray-400 hover:text-gray-500"
              title="Zmień hasło"
            >
              <Key className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="p-2 rounded-full text-gray-400 hover:text-gray-500"
              title="Wyloguj"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent 
            navigation={navigation} 
            isCurrentPath={isCurrentPath}
            invoicesMenuOpen={invoicesMenuOpen}
            setInvoicesMenuOpen={setInvoicesMenuOpen}
            casesMenuOpen={casesMenuOpen}
            setCasesMenuOpen={setCasesMenuOpen}
            departmentsMenuOpen={departmentsMenuOpen}
            setDepartmentsMenuOpen={setDepartmentsMenuOpen}
            usersMenuOpen={usersMenuOpen}
            setUsersMenuOpen={setUsersMenuOpen}
            hrMenuOpen={hrMenuOpen}
            setHrMenuOpen={setHrMenuOpen}
            settingsMenuOpen={settingsMenuOpen}
            setSettingsMenuOpen={setSettingsMenuOpen}
            logout={logout}
            navigate={navigate}
            hasNewInvoices={hasNewInvoices}
            setHasNewInvoices={setHasNewInvoices}
          />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex lg:flex-shrink-0 lg:fixed lg:top-16 lg:left-0 lg:bottom-0 transition-[width] duration-200 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className={`flex flex-col flex-1 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
          <SidebarContent 
            navigation={navigation} 
            isCurrentPath={isCurrentPath}
            invoicesMenuOpen={invoicesMenuOpen}
            setInvoicesMenuOpen={setInvoicesMenuOpen}
            casesMenuOpen={casesMenuOpen}
            setCasesMenuOpen={setCasesMenuOpen}
            departmentsMenuOpen={departmentsMenuOpen}
            setDepartmentsMenuOpen={setDepartmentsMenuOpen}
            usersMenuOpen={usersMenuOpen}
            setUsersMenuOpen={setUsersMenuOpen}
            hrMenuOpen={hrMenuOpen}
            setHrMenuOpen={setHrMenuOpen}
            settingsMenuOpen={settingsMenuOpen}
            setSettingsMenuOpen={setSettingsMenuOpen}
            logout={logout}
            navigate={navigate}
            hasNewInvoices={hasNewInvoices}
            setHasNewInvoices={setHasNewInvoices}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapsed}
          />
        </div>
      </div>

      {/* Main content */}
      <div className={`pt-16 transition-[padding] duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <main className="min-h-screen">
          <div className="py-6">
            <div className="w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* Modal zmiany hasła */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userEmail={user?.email}
      />
      <KadryEmailModal
        isOpen={showKadryEmailModal}
        onClose={() => setShowKadryEmailModal(false)}
        currentKadryEmail={user?.kadryEmail}
        onSaved={refreshUser}
      />

      {/* Chat Widget - floating widget w prawym dolnym rogu */}
      <ChatWidget />
    </div>
  );
};

const CollapsedSubmenuFlyout = ({ item, isActive, btnBase, Icon, linkClass, iconClass, isCurrentPath, hasNewInvoices, setHasNewInvoices, clearHoverTimer, scheduleClose, hoveredSubmenu, setHoveredSubmenu }) => {
  const triggerRef = useRef(null);
  const flyoutPosRef = useRef({ left: 0, top: 0 });
  const isFlyoutOpen = hoveredSubmenu === item.name;

  const openFlyout = () => {
    clearHoverTimer();
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      flyoutPosRef.current = { left: r.right, top: r.top };
    }
    setHoveredSubmenu(item.name);
  };

  const flyoutContent = isFlyoutOpen ? (
    <div
      className="fixed w-56 py-1 pl-2 bg-white rounded-r-md shadow-lg border border-gray-200 border-l-0 z-[9999]"
      style={{ left: flyoutPosRef.current.left, top: flyoutPosRef.current.top }}
      onMouseEnter={() => { clearHoverTimer(); setHoveredSubmenu(item.name); }}
      onMouseLeave={() => scheduleClose(() => setHoveredSubmenu(null))}
    >
      {item.submenu.map((subItem) => {
        const SubIcon = subItem.icon;
        const isListaFaktur = subItem.name === 'Lista faktur';
        const showNewIndicator = isListaFaktur && hasNewInvoices && !isCurrentPath(subItem.href);
        return (
          <Link
            key={subItem.name}
            to={subItem.href}
            onClick={() => isListaFaktur && setHasNewInvoices(false)}
            className={`${linkClass(subItem)} flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-4`}
          >
            <SubIcon className={`${iconClass(subItem)} mr-3 flex-shrink-0 h-5 w-5`} />
            <span className="flex-1">{subItem.name}</span>
            {showNewIndicator && <AlertCircle className="ml-2 h-4 w-4 text-red-500 flex-shrink-0" />}
            {typeof subItem.count === 'number' && (
              <span className={`ml-2 min-w-[20px] px-1.5 py-0.5 text-xs font-semibold rounded-full text-center ${
                subItem.count > 0 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
              }`}>{subItem.count}</span>
            )}
          </Link>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        className="relative"
        onMouseEnter={openFlyout}
        onMouseLeave={() => scheduleClose(() => setHoveredSubmenu(null))}
      >
        <button className={btnBase} type="button">
          <Icon className={`h-6 w-6 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
        </button>
      </div>
      {flyoutContent && createPortal(flyoutContent, document.body)}
    </>
  );
};

const SidebarContent = ({ navigation, isCurrentPath, invoicesMenuOpen, setInvoicesMenuOpen, casesMenuOpen, setCasesMenuOpen, departmentsMenuOpen, setDepartmentsMenuOpen, usersMenuOpen, setUsersMenuOpen, hrMenuOpen, setHrMenuOpen, settingsMenuOpen, setSettingsMenuOpen, logout, navigate, hasNewInvoices, setHasNewInvoices, collapsed, onToggleCollapse }) => {
  const isCollapsed = collapsed && onToggleCollapse; // desktop only
  const { theme } = useTheme();
  const isModern = theme === UI_THEMES.MODERN;
  const topActiveClass = (active) => isModern
    ? (active ? 'bg-primary-50 border-transparent text-primary-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-900')
    : (active ? 'bg-primary-100 border-primary-500 text-primary-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900');
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
  const hoverTimerRef = React.useRef(null);
  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };
  const scheduleClose = (cb) => {
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => { cb(); hoverTimerRef.current = null; }, 200);
  };

  return (
  <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
    <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto overflow-x-visible">
      <div className={`flex items-center flex-shrink-0 px-2 ${isCollapsed ? 'justify-center' : 'px-4'}`}>
        {!isCollapsed && (
          isModern
            ? <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Menu</span>
            : <span className="text-xl font-semibold text-gray-900">Menu</span>
        )}
      </div>
      <nav className={`mt-5 flex-1 space-y-1 ${isCollapsed ? 'px-2' : 'px-2'}`}>
        {navigation.map((item) => {
          const Icon = item.icon;
          const hasSubmenu = item.submenu && item.submenu.length > 0;
          const isInvoicesItem = item.name === 'Faktury';
          const isCasesItem = item.name === 'Sprawy';
          const isDepartmentsItem = item.name === 'Działy';
          const isUsersItem = item.name === 'Użytkownicy';
          const isHrItem = item.name === 'Kadry';
          const isSettingsItem = item.name === 'Ustawienia';
          const isActive = hasSubmenu 
            ? item.submenu.some(subItem => isCurrentPath(subItem.href)) || isCurrentPath(item.href)
            : isCurrentPath(item.href);
          
          const menuOpen = isInvoicesItem ? invoicesMenuOpen 
            : (isCasesItem ? casesMenuOpen 
            : (isDepartmentsItem ? departmentsMenuOpen 
            : (isUsersItem ? usersMenuOpen 
            : (isHrItem ? hrMenuOpen
            : (isSettingsItem ? settingsMenuOpen : false)))));
          const setMenuOpen = isInvoicesItem ? setInvoicesMenuOpen 
            : (isCasesItem ? setCasesMenuOpen 
            : (isDepartmentsItem ? setDepartmentsMenuOpen 
            : (isUsersItem ? setUsersMenuOpen 
            : (isHrItem ? setHrMenuOpen
            : (isSettingsItem ? setSettingsMenuOpen : () => {})))));

          const linkClass = (subItem) => {
            const isSigned = subItem?.name === 'Podpisane';
            const isOczekujace = subItem?.name === 'Oczekujące';
            const isZaksiegowane = subItem?.name === 'Zaksięgowane';
            const isSubActive = subItem ? isCurrentPath(subItem.href) : false;
            if (isModern) {
              if (isSigned) return isSubActive ? 'bg-emerald-50 border-transparent text-emerald-700' : 'border-transparent text-emerald-600 hover:bg-emerald-50';
              if (isOczekujace) return isSubActive ? 'bg-rose-50 border-transparent text-rose-700' : 'border-transparent text-rose-600 hover:bg-rose-50';
              if (isZaksiegowane) return isSubActive ? 'bg-amber-50 border-transparent text-amber-700' : 'border-transparent text-amber-600 hover:bg-amber-50';
              return isSubActive ? 'bg-primary-50 border-transparent text-primary-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-900';
            }
            if (isSigned) return isSubActive ? 'bg-green-50 border-green-500 text-green-700' : 'border-green-300 text-green-600 hover:bg-green-50 hover:text-green-900';
            if (isOczekujace) return isSubActive ? 'bg-red-50 border-red-500 text-red-700' : 'border-red-300 text-red-600 hover:bg-red-50 hover:text-red-900';
            if (isZaksiegowane) return isSubActive ? 'bg-yellow-50 border-yellow-500 text-yellow-700' : 'border-yellow-300 text-yellow-600 hover:bg-yellow-50 hover:text-yellow-900';
            return isSubActive ? 'bg-primary-50 border-primary-500 text-primary-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900';
          };
          const iconClass = (subItem) => {
            const isSigned = subItem?.name === 'Podpisane';
            const isOczekujace = subItem?.name === 'Oczekujące';
            const isZaksiegowane = subItem?.name === 'Zaksięgowane';
            const isSubActive = subItem ? isCurrentPath(subItem.href) : false;
            if (isSigned) return isSubActive ? 'text-green-500' : 'text-green-400 group-hover:text-green-500';
            if (isOczekujace) return isSubActive ? 'text-red-500' : 'text-red-400 group-hover:text-red-500';
            if (isZaksiegowane) return isSubActive ? 'text-yellow-500' : 'text-yellow-400 group-hover:text-yellow-500';
            return isSubActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500';
          };

          if (isCollapsed) {
            const btnBase = `group relative flex items-center justify-center w-full py-2.5 rounded-md border-l-4 ${topActiveClass(isActive)}`;
            if (hasSubmenu) {
              return (
                <CollapsedSubmenuFlyout
                  key={item.name}
                  item={item}
                  isActive={isActive}
                  btnBase={btnBase}
                  Icon={Icon}
                  linkClass={linkClass}
                  iconClass={iconClass}
                  isCurrentPath={isCurrentPath}
                  hasNewInvoices={hasNewInvoices}
                  setHasNewInvoices={setHasNewInvoices}
                  clearHoverTimer={clearHoverTimer}
                  scheduleClose={scheduleClose}
                  hoveredSubmenu={hoveredSubmenu}
                  setHoveredSubmenu={setHoveredSubmenu}
                />
              );
            }
            return (
              <Link key={item.name} to={item.href} className={btnBase} title={item.name}>
                <Icon className={`h-6 w-6 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
              </Link>
            );
          }

          return (
            <div key={item.name}>
              {hasSubmenu ? (
                <>
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className={`${topActiveClass(isActive)} group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md border-l-4`}
                  >
                    <div className="flex items-center">
                      <Icon className={`${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'} mr-3 flex-shrink-0 h-6 w-6`} />
                      {item.name}
                    </div>
                    {menuOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </button>
                  {menuOpen && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isListaFaktur = subItem.name === 'Lista faktur';
                        const showNewIndicator = isListaFaktur && hasNewInvoices && !isCurrentPath(subItem.href);
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={() => { if (isListaFaktur) setHasNewInvoices(false); }}
                            className={`${linkClass(subItem)} group flex items-center px-2 py-2 text-sm font-medium rounded-md border-l-4`}
                          >
                            <SubIcon className={`${iconClass(subItem)} mr-3 flex-shrink-0 h-5 w-5`} />
                            <span className="flex-1">{subItem.name}</span>
                            {showNewIndicator && <AlertCircle className="ml-2 h-4 w-4 text-red-500 flex-shrink-0" />}
                            {typeof subItem.count === 'number' && (
                              <span className={`ml-2 min-w-[20px] px-1.5 py-0.5 text-xs font-semibold rounded-full text-center ${
                                subItem.name === 'Podpisane'
                                  ? subItem.count > 0 ? 'bg-green-100 text-green-700' : 'bg-green-50 text-green-600'
                                  : subItem.name === 'Oczekujące'
                                    ? subItem.count > 0 ? 'bg-red-100 text-red-700' : 'bg-red-50 text-red-600'
                                    : subItem.name === 'Zaksięgowane'
                                      ? subItem.count > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-yellow-50 text-yellow-600'
                                      : subItem.count > 0 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                              }`}>{subItem.count}</span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href}
                  className={`${topActiveClass(isActive)} group flex items-center px-2 py-2 text-sm font-medium rounded-md border-l-4`}
                >
                  <Icon className={`${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'} mr-3 flex-shrink-0 h-6 w-6`} />
                  {item.name}
                </Link>
              )}
            </div>
          );
        })}
      </nav>
    </div>
    {isCollapsed && (
      <div className="flex-shrink-0 px-2 py-2 border-t border-gray-200">
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-full py-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          title="Rozwiń menu"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    )}
    {!isCollapsed && onToggleCollapse && (
      <div className="flex-shrink-0 px-2 py-2 border-t border-gray-200">
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-full py-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          title="Zwiń menu"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>
    )}
  </div>
  );
};

export default Layout;
